/**
 * buildEngineHandle — constructs the public Engine handle object.
 *
 * Extracted from createEngine.tsx to keep that file as a thin orchestrator.
 * All method implementations live here; the deps bundle wires them to the
 * subsystem managers created in createEngine.
 *
 * @see packages/engine/src/api/createEngine.tsx — orchestration entry point
 * @see packages/engine/src/api/types/engine.ts  — Engine interface contract
 */

import type {
  Engine,
  EngineEventMap,
  EngineEventHandler,
  EngineStateSnapshot,
  CameraConfig,
  CameraViewpoint,
  CameraPreset,
  AutoRotateConfig,
  MaterialApplyOptions,
  MaterialChangeEvent,
  MaterialTextureOptions,
  TextureVariantChangeEvent,
  SunlightConfig,
  ShadowMode,
  ShadowConfig,
  EnvironmentConfig,
  EnvironmentOverrides,
  ViewerSettingsConfig,
  ViewerSettingsState,
  ViewerSettingsPresetId,
  SteeringPosition,
  WheelState,
} from './types';
import type { IBLConfig } from '../environment/IBLManager';
import type { EventEmitter } from './EventEmitter';
import type { createEngineStore } from '../store/engineStore';
import { getSnapshot } from '../store/engineStore';
import type { AssetCache } from '../core/AssetCache';
import type { AssetLoader } from '../core/AssetLoader';
import type { SceneManager } from '../core/SceneManager';
import type { MaterialManager } from '../core/MaterialManager';
import type { SunlightManager } from '../core/SunlightManager';
import type { GroundShadowManager } from '../core/GroundShadowManager';
import type { IBLManager } from '../environment/IBLManager';
import type { EnvironmentManager } from '../environment/EnvironmentManager';
import type { PresetManager } from '../environment/PresetManager';
import type { ViewerSettingsManager } from '../core/ViewerSettingsManager';
import type { AnimationManager } from '../core/AnimationManager';
import type { DimensionsManager } from '../core/DimensionsManager';
import type { ObjectManager } from '../core/ObjectManager';
import type { PreloadManager } from '../core/PreloadManager';
import type { LightSignatureManager } from '../core/LightSignatureManager';
import type { WheelManager } from '../core/WheelManager';
import type { PanoManager } from '../core/PanoManager';
import type { CameraManagerRef } from '../core/CameraManager';
import type { DimensionsConfig, DimensionsState } from './types/dimensions';
import type {
  LoadAssetConfig,
  BaseNodeConfig,
  RevealOptions,
  HideAssetOptions,
  SwapOptions,
  ObjectManagerState,
  ManagedObjectState,
} from './types/objectManager';
import type { PreloadEntry, PreloadPresetConfig, PreloadProgress } from './types/preload';
import type { LightSignatureState } from './types/lightSignature';
import type { ResolvedColorDefinition } from '@ims/shared';
import type { RefObject } from 'react';

// ---------------------------------------------------------------------------
// Shared mutable state between handle methods and createEngine.tsx closures
// ---------------------------------------------------------------------------

/**
 * Mutable state box shared between the Engine handle and createEngine.tsx.
 *
 * Using a box (object with properties) rather than raw primitives ensures
 * that closures defined in createEngine.tsx (e.g. the DOM click handler)
 * always read the CURRENT value after `dispose()` or `enableDimensions()`
 * mutates the properties.
 */
export interface EngineInternalState {
  /** Set to true in dispose(); checked in DOM click handler to bail out. */
  disposed: boolean;
  /**
   * Set true by enableDimensions(), false by disableDimensions().
   * Checked in setEnvironment / setPreset to auto-enable/disable the overlay
   * when the user switches to / away from the 'studio' environment.
   */
  dimensionsActivated: boolean;
}

// ---------------------------------------------------------------------------
// Deps bundle
// ---------------------------------------------------------------------------

export interface BuildEngineHandleArgs {
  state: EngineInternalState;
  container: HTMLElement;
  emitter: EventEmitter;
  store: ReturnType<typeof createEngineStore>;
  cache: AssetCache;
  loader: AssetLoader;
  sceneManager: SceneManager;
  materialManager: MaterialManager;
  sunlightManager: SunlightManager;
  groundShadowManager: GroundShadowManager;
  iblManager: IBLManager;
  environmentManager: EnvironmentManager;
  presetManager: PresetManager;
  viewerSettingsManager: ViewerSettingsManager;
  animationManager: AnimationManager;
  dimensionsManager: DimensionsManager;
  objectManager: ObjectManager;
  preloadManager: PreloadManager;
  lightSignatureManager: LightSignatureManager;
  wheelManager: WheelManager;
  panoManager: PanoManager;
  cameraRef: RefObject<CameraManagerRef | null>;
  debugCubeSetVisible: { current: (v: boolean) => void };
  handlePointerDown: (e: PointerEvent) => void;
  handleContainerClick: (e: MouseEvent) => void;
}

// ---------------------------------------------------------------------------
// buildEngineHandle
// ---------------------------------------------------------------------------

export function buildEngineHandle(args: BuildEngineHandleArgs): Engine {
  const {
    state,
    container,
    emitter,
    store,
    cache,
    loader,
    sceneManager,
    materialManager,
    sunlightManager,
    groundShadowManager,
    iblManager,
    environmentManager,
    presetManager,
    viewerSettingsManager,
    animationManager,
    dimensionsManager,
    objectManager,
    preloadManager,
    lightSignatureManager,
    wheelManager,
    panoManager,
    cameraRef,
    debugCubeSetVisible,
    handlePointerDown,
    handleContainerClick,
  } = args;

  // When an external GLB (rim variant, accessory) mounts, scan it for
  // steering nodes so WheelManager can steer all visible wheels — not just
  // the ones baked into the base model GLB.
  objectManager.onObjectMounted = (_id, scene) => {
    wheelManager.scanAdditionalScene(scene);
  };

  const engine: Engine = {
    dispose() {
      if (state.disposed) return;
      state.disposed = true;

      container.removeEventListener('pointerdown', handlePointerDown);
      container.removeEventListener('click', handleContainerClick);
      loader.dispose();
      objectManager.dispose();
      preloadManager.dispose();
      dimensionsManager.dispose();
      animationManager.dispose();
      wheelManager.dispose();
      panoManager.dispose();
      lightSignatureManager.dispose();
      materialManager.dispose();
      sunlightManager.dispose();
      groundShadowManager.dispose();
      iblManager.dispose();
      presetManager.dispose();
      environmentManager.dispose();
      viewerSettingsManager.dispose();
      sceneManager.dispose();
      cache.clear();
      store.getState().reset();
      emitter.dispose();
    },

    resize() {
      sceneManager.resize();
    },

    on<K extends keyof EngineEventMap>(event: K, handler: EngineEventHandler<K>): () => void {
      return emitter.on(event, handler);
    },

    off<K extends keyof EngineEventMap>(event: K, handler: EngineEventHandler<K>): void {
      emitter.off(event, handler);
    },

    getState(): EngineStateSnapshot {
      return getSnapshot(store);
    },

    // -------------------------------------------------------------------------
    // Camera System
    // -------------------------------------------------------------------------

    setCamera(cfg: Partial<CameraConfig>) {
      cameraRef.current?.setCamera(cfg);
    },

    setViewpoint(viewpoint: CameraViewpoint) {
      cameraRef.current?.setViewpoint(viewpoint);
    },

    setCameraPreset(preset: CameraPreset) {
      cameraRef.current?.setCameraPreset(preset);
    },

    resetCamera() {
      cameraRef.current?.reset();
    },

    setAutoRotate(cfg: Partial<AutoRotateConfig>) {
      cameraRef.current?.setAutoRotate(cfg);
    },

    // -------------------------------------------------------------------------
    // Material System
    // -------------------------------------------------------------------------

    async setMaterial(target: string, opts: MaterialApplyOptions): Promise<void> {
      // Sync path: preset swap + color/type lerp (only for preset-tracked materials)
      if (materialManager.has(target)) {
        materialManager.apply(target, opts);
      }

      // Async path: patch texture channels and/or apply colorCode on atlas-tracked materials
      if (materialManager.hasAtlas(target)) {
        const textures = opts.textures;
        await materialManager.applyAtlasVariant(target, {
          diffuseUrl: textures?.diffuseUrl,
          normalUrl: textures?.normalUrl,
          metallicRoughnessUrl: textures?.metallicRoughnessUrl,
          colorCode: opts.colorCode,
        });
        if (textures) {
          const texEvent: TextureVariantChangeEvent = {
            target,
            diffuseUrl: textures.diffuseUrl,
            normalUrl: textures.normalUrl,
            metallicRoughnessUrl: textures.metallicRoughnessUrl,
          };
          emitter.emit('textureVariantChanged', texEvent);
        }
      } else if (opts.textures) {
        // Target is in preset track only — still forward textures to atlas
        // (legacy path: material in both tracks)
        const { diffuseUrl, normalUrl, metallicRoughnessUrl } = opts.textures;
        await materialManager.applyAtlasVariant(target, {
          diffuseUrl,
          normalUrl,
          metallicRoughnessUrl,
        });
        const texEvent: TextureVariantChangeEvent = {
          target,
          diffuseUrl,
          normalUrl,
          metallicRoughnessUrl,
        };
        emitter.emit('textureVariantChanged', texEvent);
      }

      // Warn if target is not managed at all
      if (!materialManager.has(target) && !materialManager.hasAtlas(target)) {
        materialManager.apply(target, opts); // triggers the devWarn with helpful hints
      }

      // Emit materialChanged with full context (includes texture info)
      const event: MaterialChangeEvent = {
        target,
        preset: opts.preset,
        colorCode: opts.colorCode,
        type: opts.type,
        textures: opts.textures as MaterialTextureOptions | undefined,
      };
      emitter.emit('materialChanged', event);

      // Track active color in store by colorCode
      if (opts.colorCode) {
        const colors = store.getState().availableColors;
        const match = colors.find((c) => c.primary.colorCode === opts.colorCode);
        if (match) store.getState().setActiveColorId(match.id);
      }
    },

    resetMaterial(target: string) {
      // Reset preset track (restore original GLB material)
      materialManager.reset(target);
      // Reset atlas track if it exists (restore original GLB textures)
      if (materialManager.hasAtlas(target)) {
        materialManager.resetAtlas(target);
      }
      store.getState().setActiveColorId(null);
    },

    setAvailableColors(colors: ResolvedColorDefinition[]) {
      store.getState().setAvailableColors(colors);
    },

    getAvailableColors(): ResolvedColorDefinition[] {
      return store.getState().availableColors;
    },

    onMaterialChange(cb: (ev: MaterialChangeEvent) => void): () => void {
      return emitter.on('materialChanged', cb);
    },

    // -------------------------------------------------------------------------
    // Sunlight System
    // -------------------------------------------------------------------------

    setSunlight(config: string | SunlightConfig) {
      sunlightManager.apply(config);
    },

    setShadow(config: ShadowMode | ShadowConfig) {
      groundShadowManager.apply(config);
    },

    // -------------------------------------------------------------------------
    // IBL System (standalone — independent of environment)
    // -------------------------------------------------------------------------

    setIBL(config: IBLConfig) {
      iblManager.apply(config);
    },

    // -------------------------------------------------------------------------
    // Environment System (visual scene framing only — no sunlight/shadow/IBL)
    // -------------------------------------------------------------------------

    setEnvironment(config: string | EnvironmentConfig, overrides?: EnvironmentOverrides) {
      environmentManager.apply(config, overrides);
      const envState = environmentManager.getState();
      // Auto-enable dimensions only in studio, disable in all other environments.
      if (state.dimensionsActivated) {
        if (envState.id === 'studio') dimensionsManager.enable();
        else dimensionsManager.disable();
      }
      emitter.emit('environmentChanged', { id: envState.id });
    },

    // -------------------------------------------------------------------------
    // Preset System (orchestrates all subsystems from named combos)
    // -------------------------------------------------------------------------

    setPreset(presetId: string) {
      presetManager.apply(presetId);
      const envState = environmentManager.getState();
      // Auto-enable dimensions only in studio, disable in all other environments.
      if (state.dimensionsActivated) {
        if (envState.id === 'studio') dimensionsManager.enable();
        else dimensionsManager.disable();
      }
      emitter.emit('environmentChanged', { id: envState.id });
    },

    setDebugCube(show: boolean) {
      debugCubeSetVisible.current(show);
    },

    // -------------------------------------------------------------------------
    // Animation System
    // -------------------------------------------------------------------------

    playAnimation(name: string, options?) {
      if (name === '*') {
        animationManager.playAll(options);
      } else {
        animationManager.play(name, options);
      }
    },

    stopAnimation(name: string) {
      if (name === '*') {
        animationManager.stopAll();
      } else {
        animationManager.stop(name);
      }
    },

    jumpToAnimationEnd(name, direction) {
      if (name === '*') {
        animationManager.jumpAll(direction);
      } else {
        animationManager.jump(name, direction);
      }
    },

    getAnimationState() {
      return animationManager.getState();
    },

    // -------------------------------------------------------------------------
    // Viewer Settings (PostFX + Renderer Control)
    // -------------------------------------------------------------------------

    setViewerSettings(cfg: ViewerSettingsPresetId | ViewerSettingsConfig) {
      viewerSettingsManager.apply(cfg);
      emitter.emit(
        'viewerSettingsChanged',
        viewerSettingsManager.getState() as ViewerSettingsState,
      );
    },

    getViewerSettings(): ViewerSettingsState {
      return { ...viewerSettingsManager.getState() };
    },

    // -------------------------------------------------------------------------
    // Dimensions Overlay
    // -------------------------------------------------------------------------

    toggleDimensions(force?: boolean) {
      dimensionsManager.toggle(force);
    },

    setDimensions(config: DimensionsConfig) {
      dimensionsManager.setDimensions(config);
    },

    getDimensionsState(): DimensionsState {
      return dimensionsManager.getState();
    },

    enableDimensions() {
      state.dimensionsActivated = true;
      // Only activate now if already in the studio environment.
      // Otherwise setPreset/setEnvironment will enable it when studio is activated.
      if (environmentManager.getState().id === 'studio') {
        dimensionsManager.enable();
      }
    },

    disableDimensions() {
      state.dimensionsActivated = false;
      dimensionsManager.disable();
    },

    // -------------------------------------------------------------------------
    // Object Management
    // -------------------------------------------------------------------------

    async loadAsset(assetConfig: LoadAssetConfig) {
      objectManager.register(assetConfig);
      await objectManager.load(assetConfig.id);
    },

    registerBaseNode(config: BaseNodeConfig) {
      objectManager.registerBaseNode(config);
    },

    showAsset(id: string, options?: RevealOptions) {
      objectManager.show(id, options);
    },

    hideAsset(id: string, options?: HideAssetOptions) {
      objectManager.hide(id, options);
    },

    swapAsset(groupId: string, newId: string, options?: SwapOptions) {
      objectManager.swap(groupId, newId, options);
    },

    getObjectState(): ObjectManagerState {
      return objectManager.getState();
    },

    getObjectInfo(id: string): ManagedObjectState | undefined {
      return objectManager.getObjectState(id);
    },

    // -------------------------------------------------------------------------
    // Asset Preloading
    // -------------------------------------------------------------------------

    preload(entries: PreloadEntry[]) {
      preloadManager.preload(entries);
    },

    preloadPreset(config: PreloadPresetConfig) {
      preloadManager.preloadPreset(config);
    },

    getPreloadProgress(): PreloadProgress {
      return preloadManager.getProgress();
    },

    // -------------------------------------------------------------------------
    // Light Signature System
    // -------------------------------------------------------------------------

    setLightSignature(on: boolean, animated = true) {
      lightSignatureManager.setOn(on, animated);
    },

    getLightSignatureState(): LightSignatureState {
      return lightSignatureManager.getState();
    },

    resetLightSignature() {
      lightSignatureManager.restoreOriginals();
      emitter.emit('lightSignatureChanged', { isOn: false, animated: false });
    },

    setLightSignatureTexture(url: string): Promise<void> {
      return lightSignatureManager.applyPositionTexture(url);
    },

    // -------------------------------------------------------------------------
    // Wheel Animation System
    // -------------------------------------------------------------------------

    setWheelRotation(active: boolean, animated = true) {
      wheelManager.setRotation(active, animated);
    },

    setWheelSteering(position: SteeringPosition, animated = true) {
      wheelManager.setSteering(position, animated);
    },

    setWheelSteeringAngle(angleDeg: number, animated = true) {
      wheelManager.setSteeringAngle(angleDeg, animated);
    },

    getWheelState(): WheelState {
      return wheelManager.getState();
    },

    // -------------------------------------------------------------------------
    // Interior Panorama System
    // -------------------------------------------------------------------------

    showPano(url: string) {
      panoManager.show(url);
    },

    switchPano(url: string) {
      panoManager.switchTo(url);
    },

    hidePano() {
      panoManager.hide();
    },

    getPanoState() {
      return panoManager.getState();
    },
  };

  return engine;
}
